import richTextUtils from "../../utils/richtext.utils";
import FileUploaderModal from "@plugins/file-uploader-v2/components/FileUploaderModal.vue";
import UgcFileService from "../../service/ugc.file.service";
import DOMUtils from "@utils/dom.utils";
import { MediaPlayerEventBus } from "@plugins/media/service/media.player.event.bus";
import i18n from "@plugins/i18n";
import { FileResponse, FileUploadEntry, UploadFlow } from "@plugins/file-uploader-v2/types.ts";
import randomUtils from "@utils/random.utils.ts";
import { ref } from "vue";
import FileUploaderHidden from "@plugins/file-uploader-v2/components/FileUploaderHidden.vue";
import { MEDIA_RECORD_TYPE, MEDIA_TYPE } from "@/plugins/media/media.constant";

interface UgcResponseData {
  error: boolean;
  fileDuration: number;
  fileType: string | null;
  type: string;
  uuid: string;
}

export default function (editor) {
  const MEDIA_SELECTOR = ".sls-media, .sls-media-object";

  const richTextEditor = editor.options.get("richTextEditor");
  const pluginEvent = editor.options.get("pluginEvent");
  let fileService: UgcFileService | null = null;
  const uploadedFile = ref<FileUploadEntry | null>(null);

  editor.ui.registry.addButton("file_upload", {
    tooltip: i18n.t("tinymce_plugin_media_upload_button_tooltip"), // TODO: to move to properties bundle
    icon: "file-upload",
    onAction: function () {
      openModal();
    },
  });

  editor.addCommand("mceUploaderMediaDelete", (targetNode) => {
    richTextUtils.removeNodeFromEditor(editor, targetNode);
  });

  editor.on("click", (e) => {
    const target = e.target;
    if (target.closest("button.trash") && target.closest("button.trash").length !== 0) {
      const targetNode = target.closest(".sls-media-wrapper");
      if (targetNode) {
        // delete from attached files list saved in editor
        const uuid = targetNode.querySelector(".sls-media-tag").getAttribute("data-uuid");
        const remainingFiles = richTextEditor.attachedFiles.filter((file) => file.uuid !== uuid);
        richTextEditor.attachedFiles.splice(
          0,
          richTextEditor.attachedFiles.length,
          ...remainingFiles,
        );
        //to go throught execCommand, so that tinymce can control the delete event
        editor.execCommand("mceUploaderMediaDelete", targetNode);
      }
    }

    // cater for cases when have undo action
    handlePluginRerender(target);
  });

  (function init() {
    if (
      richTextEditor.config &&
      richTextEditor.config.plugins &&
      richTextEditor.config.plugins["fileUpload"]
    ) {
      richTextEditor.plugins["fileUpload"] = {
        renderInEditor,
        cleanUp,
        containPluginElements,
        handleFileUploadHidden,
      };
      const serviceConfig = richTextEditor.config.plugins.fileUpload.fileServiceConfig;
      if (serviceConfig.type === "ugc") {
        fileService = new UgcFileService(serviceConfig);
      } else if (serviceConfig.type === "mo") {
        // init media object file service
      }
    } else {
      // TODO: some warning for missing config
    }
  })();

  function openModal() {
    richTextEditor.$modal.show(
      FileUploaderModal,
      {
        uploadFlow: richTextEditor.config.plugins.fileUpload.uploadFlow ?? UploadFlow.ANY_FILE,
        modalId: randomUtils.randomId(),
      },
      {
        "update:fileEntry": (fileResponse: FileResponse) => {
          handleFileUpload(fileResponse);
        },
        fileChanged: (file: FileUploadEntry) => {
          uploadedFile.value = file;
        },
      },
      true,
    );
  }

  async function handleFileUploadHidden(data) {
    uploadedFile.value = data;
    const file = data.file;
    const fileEntry = {
      file: file,
    };

    // Step 1: On create, generate a new UGC placeholder to get ugcUuid before calling generate-scan-url.
    // On edit, the uuid is already known — no API call needed, just use it directly.
    if (data.uuid && !data.createAsNew) {
      // Edit path: record already exists, pass its uuid straight into FileUploaderHidden.
      richTextEditor.$modal.show(
        FileUploaderHidden,
        {
          uploadFlow: UploadFlow.IMG,
          fileEntry: fileEntry,
          recordType: MEDIA_RECORD_TYPE.UGC,
          recordUuid: data.uuid,
        },
        {
          "update:fileEntry": (fileResponse: FileResponse) => {
            handleFileInsert(fileResponse, {
              uuid: data.uuid,
              type: data.type,
              fileType: data.fileType,
            });
          },
        },
        true,
      );
      return;
    }

    try {
      const ugcUuid = (await fileService!.preCreate()) as string;
      richTextEditor.$modal.show(
        FileUploaderHidden,
        {
          uploadFlow: UploadFlow.IMG,
          fileEntry: fileEntry,
          resourceType: MEDIA_RECORD_TYPE.UGC,
          resourceUuid: ugcUuid,
        },
        {
          "update:fileEntry": (fileResponse: FileResponse) => {
            // UGC record already exists — just wire it into the editor
            handleFileInsert(fileResponse, { uuid: ugcUuid });
          },
        },
        true,
      );
    } catch {
      richTextEditor.passiveApiFailHandler();
    }
  }

  function handleFileInsert(
    fileResponse: FileResponse,
    ugcData: { uuid: string; type?: string; fileType?: string },
  ) {
    MediaPlayerEventBus.$emit("newFileDuration", {
      questionAttemptUuid: ugcData.uuid,
      duration: 0,
    });
    richTextEditor.attachedFiles.push({
      uuid: ugcData.uuid,
      file: uploadedFile.value?.file,
    });
    richTextEditor.$modal.removeAll();
    richTextUtils.insertInlineAttachment(editor, {
      uuid: ugcData.uuid,
      mediaType: ugcData.type || ugcData.fileType || MEDIA_TYPE.IMAGE,
      fileName: fileResponse.name,
      type: fileService!.getType(),
      nonPreviewable: true,
    });
    richTextUtils.renderInlineMedia(editor, ".inline-placeholder", {
      onOpenPdfInNewTab: openPdfInNewTab,
    });
    editor.save();
    if (pluginEvent) pluginEvent.$emit("mediaRendered");
  }

  async function handleFileUpload(
    fileResponse: FileResponse,
    oldUuid?: string,
    createAsNew?: boolean,
  ) {
    const data = await saveIfNotCreateUgc(fileResponse, oldUuid, createAsNew);
    handleFileInsert(fileResponse, {
      uuid: data.uuid,
      type: data.type,
      fileType: data.fileType ?? undefined,
    });
  }

  function renderInEditor(rerender = false) {
    richTextUtils.renderInlineMedia(
      editor,
      MEDIA_SELECTOR,
      {
        onOpenPdfInNewTab: openPdfInNewTab,
      },
      rerender,
    );
  }

  function openPdfInNewTab(uuid) {
    const routeData = richTextEditor.$router.resolve({
      name: "PdfView",
      params: { uuid: uuid },
    });
    window.open(routeData.href, "_blank");
  }

  function cleanUp(content) {
    const container = document.createElement("div");
    const containerAfterCleanUp = document.createElement("div");
    container.innerHTML = content;
    containerAfterCleanUp.innerHTML = content;

    // remove placeholder
    DOMUtils.elOf(containerAfterCleanUp, ".sls-media-wrapper").forEach((element) => {
      if (DOMUtils.elOf(element, ".inline-placeholder").length !== 0) {
        element.remove();
      }
    });
    DOMUtils.elOf(containerAfterCleanUp, ".inline-placeholder").forEach((element) => {
      element.remove();
    });
    DOMUtils.elOf(container, ".sls-media-tag[data-type='ugc']").forEach((element) => {
      cleanUpElement(containerAfterCleanUp, element);
    });

    DOMUtils.elOf(containerAfterCleanUp, "br[data-mce-bogus]").forEach((element) => {
      element.remove();
    });

    return containerAfterCleanUp.innerHTML;
  }

  function cleanUpElement(container, element) {
    const uuid = element.getAttribute("data-uuid");
    const media = DOMUtils.elOf(container, `.sls-media-tag[data-uuid='${uuid}']`)[0];
    const mediaWrapper = media.closest(".sls-media-wrapper");

    // switch back the class
    DOMUtils.removeClass(element, "sls-media-tag");
    DOMUtils.addClass(element, "sls-media");
    mediaWrapper.replaceWith(element);
  }

  function containPluginElements(container) {
    return container.querySelectorAll(".sls-media, .sls-media-object, figure.media").length > 0;
  }

  function handlePluginRerender(target) {
    let mediaComponent = target.closest(".media-component");
    if (!mediaComponent || mediaComponent.length === 0 || mediaComponent.__vue__) return;

    const targetNode = mediaComponent.closest(".sls-media-wrapper")
      ? DOMUtils.elOf(
          mediaComponent.closest(".sls-media-wrapper"),
          ".sls-media-tag[data-type='ugc']",
        )[0]
      : null;
    if (!targetNode) return;

    const parentMediaComponent = mediaComponent.closest(".paragraph");
    const targetComponentIndex = DOMUtils.elOf(parentMediaComponent, ".media-component").indexOf(
      mediaComponent,
    );

    cleanUpElement(editor.getBody(), targetNode);
    renderInEditor(true);

    mediaComponent = DOMUtils.elOf(parentMediaComponent, ".media-component")[targetComponentIndex];
    const mediaVueComponent = mediaComponent.__vue__;
    if (mediaVueComponent) {
      // enable drawing plugin
      if (pluginEvent) pluginEvent.$emit("mediaRendered");
    }
  }

  /**
   * Function to save (if existing) or create (if new) UGC from uploader.
   *
   * @param data
   * @returns {Promise}
   */
  async function saveIfNotCreateUgc(
    fileResponse: FileResponse,
    oldUuid?: string,
    createAsNew?: boolean,
  ): Promise<UgcResponseData> {
    if (!fileService) {
      throw new Error("FileService is not initialized");
    }

    try {
      const responseData = (
        oldUuid && !createAsNew
          ? await fileService.saveMediaFile(oldUuid, fileResponse)
          : await fileService.create(fileResponse)
      ) as UgcResponseData;

      if ((responseData as { code?: string }).code === "error") {
        richTextEditor.passiveApiFailHandler();
        throw responseData;
      }

      return responseData;
    } catch (error) {
      richTextEditor.passiveApiFailHandler();
      throw error;
    }
  }
}
